import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminGoalResponse, type ApiError } from '../../lib/api';

type CreateForm = {
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
};

type EditState = {
  id: number;
  label: string;
  sortOrder: number;
  isActive: boolean;
} | null;

const defaultCreateForm: CreateForm = {
  code: '',
  label: '',
  sortOrder: 0,
  isActive: true,
};

export function AdminGoalsPage() {
  const qc = useQueryClient();

  const [createForm, setCreateForm] = useState<CreateForm>(defaultCreateForm);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editing, setEditing] = useState<EditState>(null);
  const [deleteError, setDeleteError] = useState<{ id: number; message: string } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'goals'],
    queryFn: () => api.adminListGoals(),
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['admin', 'goals'] });
    qc.invalidateQueries({ queryKey: ['goals-for-filter'] });
  }

  const createMutation = useMutation({
    mutationFn: () =>
      api.adminCreateGoal({
        code: createForm.code.trim(),
        label: createForm.label.trim(),
        sortOrder: createForm.sortOrder,
        isActive: createForm.isActive,
      }),
    onSuccess: () => {
      setCreateForm(defaultCreateForm);
      setCreateError(null);
      invalidateAll();
    },
    onError: (err: ApiError) => {
      setCreateError(
        err.code === 'DUPLICATE_CODE'
          ? 'Код уже существует, выберите другой'
          : err.message || 'Ошибка при создании цели',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: { label: string; sortOrder: number; isActive: boolean };
    }) => api.adminUpdateGoal(id, body),
    onSuccess: () => {
      setEditing(null);
      invalidateAll();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.adminDeleteGoal(id),
    onSuccess: (_data, id) => {
      if (deleteError?.id === id) setDeleteError(null);
      invalidateAll();
    },
    onError: (err: ApiError, id: number) => {
      if (err.code === 'GOAL_IN_USE') {
        setDeleteError({ id, message: 'Цель используется в идеях, удаление невозможно' });
      } else {
        setDeleteError({ id, message: err.message || 'Ошибка при удалении' });
      }
    },
  });

  function handleDelete(goal: AdminGoalResponse) {
    if (!window.confirm(`Удалить цель «${goal.label}»?`)) return;
    setDeleteError(null);
    deleteMutation.mutate(goal.id);
  }

  function handleEditStart(goal: AdminGoalResponse) {
    setEditing({
      id: goal.id,
      label: goal.label,
      sortOrder: goal.sort_order,
      isActive: goal.is_active,
    });
    setDeleteError(null);
  }

  function handleEditSave() {
    if (!editing) return;
    updateMutation.mutate({
      id: editing.id,
      body: { label: editing.label, sortOrder: editing.sortOrder, isActive: editing.isActive },
    });
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createForm.code.trim() || !createForm.label.trim()) return;
    setCreateError(null);
    createMutation.mutate();
  }

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto">
      <header className="flex items-center justify-between mb-6">
        <Link to="/admin" className="text-sm opacity-70">
          ← Админка
        </Link>
        <h1 className="text-xl font-bold">Цели маркетинга</h1>
        <div className="w-20" />
      </header>

      {/* Create form */}
      <form
        onSubmit={handleCreate}
        className="rounded-2xl bg-tg-section-bg p-4 flex flex-col gap-3 mb-6"
      >
        <p className="text-sm font-semibold opacity-80">Новая цель</p>

        <input
          type="text"
          value={createForm.code}
          onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value }))}
          placeholder="лат. uniq, 1-64"
          className="w-full px-3 py-2 rounded-xl bg-tg-bg text-sm border border-tg-hint/30 focus:outline-none"
        />

        <input
          type="text"
          value={createForm.label}
          onChange={(e) => setCreateForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="Название цели"
          className="w-full px-3 py-2 rounded-xl bg-tg-bg text-sm border border-tg-hint/30 focus:outline-none"
        />

        <div className="flex items-center gap-3">
          <input
            type="number"
            value={createForm.sortOrder}
            onChange={(e) => setCreateForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
            placeholder="Порядок"
            className="w-24 px-3 py-2 rounded-xl bg-tg-bg text-sm border border-tg-hint/30 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={createForm.isActive}
              onChange={(e) => setCreateForm((f) => ({ ...f, isActive: e.target.checked }))}
              className="w-4 h-4"
            />
            Активна
          </label>
        </div>

        {createError && <div className="text-sm text-red-500">{createError}</div>}

        <button
          type="submit"
          disabled={createMutation.isPending || !createForm.code.trim() || !createForm.label.trim()}
          className="px-4 py-2 rounded-xl bg-tg-button text-tg-button-text text-sm font-medium disabled:opacity-40"
        >
          {createMutation.isPending ? 'Добавление...' : 'Добавить'}
        </button>
      </form>

      {/* Loading */}
      {isLoading && <p className="text-sm opacity-60 text-center py-8">Загрузка...</p>}

      {/* Error */}
      {!isLoading && error && (
        <div className="text-center py-8">
          <p className="text-sm opacity-70 mb-3">Не удалось загрузить цели</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 rounded-xl bg-tg-button text-tg-button-text text-sm"
          >
            Повторить
          </button>
        </div>
      )}

      {/* Empty */}
      {data && data.goals.length === 0 && (
        <p className="text-sm opacity-60 text-center py-8">Целей пока нет — добавьте первую выше</p>
      )}

      {/* List */}
      {data && data.goals.length > 0 && (
        <div className="space-y-2">
          {data.goals.map((goal) => {
            const isEditingThis = editing?.id === goal.id;

            return (
              <div key={goal.id} className="rounded-xl bg-tg-section-bg p-3 flex flex-col gap-2">
                {isEditingThis ? (
                  /* Edit mode */
                  <>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs opacity-50 flex-shrink-0">
                        {goal.code}
                      </span>
                    </div>

                    <input
                      type="text"
                      value={editing.label}
                      onChange={(e) =>
                        setEditing((prev) => prev && { ...prev, label: e.target.value })
                      }
                      className="w-full px-3 py-2 rounded-xl bg-tg-bg text-sm border border-tg-hint/30 focus:outline-none"
                      placeholder="Название"
                    />

                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={editing.sortOrder}
                        onChange={(e) =>
                          setEditing(
                            (prev) => prev && { ...prev, sortOrder: Number(e.target.value) },
                          )
                        }
                        className="w-24 px-3 py-2 rounded-xl bg-tg-bg text-sm border border-tg-hint/30 focus:outline-none"
                        placeholder="Порядок"
                      />
                      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={editing.isActive}
                          onChange={(e) =>
                            setEditing((prev) => prev && { ...prev, isActive: e.target.checked })
                          }
                          className="w-4 h-4"
                        />
                        Активна
                      </label>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleEditSave}
                        disabled={updateMutation.isPending}
                        className="flex-1 px-3 py-1.5 rounded-xl bg-tg-button text-tg-button-text text-xs font-medium disabled:opacity-40"
                      >
                        {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="px-3 py-1.5 rounded-xl bg-tg-bg text-xs border border-tg-hint/30 opacity-70 hover:opacity-100"
                      >
                        Отмена
                      </button>
                    </div>
                  </>
                ) : (
                  /* View mode */
                  <>
                    <div className="flex items-start gap-2">
                      <span className="font-mono text-xs opacity-50 mt-0.5 flex-shrink-0">
                        {goal.code}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug">{goal.label}</p>
                        <p className="text-xs opacity-50 mt-0.5">
                          порядок: {goal.sort_order} · {goal.is_active ? 'активна' : 'скрыта'}
                        </p>
                      </div>
                    </div>

                    {deleteError?.id === goal.id && (
                      <div className="text-sm text-red-500">{deleteError.message}</div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditStart(goal)}
                        className="px-2 py-1 rounded text-xs bg-tg-section-bg border border-tg-hint opacity-70 hover:opacity-100"
                      >
                        ✎ Редактировать
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(goal)}
                        disabled={deleteMutation.isPending}
                        className="px-2 py-1 rounded text-xs bg-tg-section-bg border border-tg-hint opacity-70 hover:opacity-100 disabled:opacity-40"
                      >
                        ✕ Удалить
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
